import { c as _c } from "react-compiler-runtime";
import { useMemo } from 'react';
import I18n from 'I18n';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import { combineFilters, getAggregatedFilterGroups } from 'framework-listing-lib/utils/filters';
import { DEFAULT_PAGE_SIZE } from 'framework-listing-lib/constants/pagination';
import { ASC, DESC } from 'framework-listing-lib/constants/sorts';
import useQuickFilters from 'framework-listing-lib/hooks/useQuickFilters';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { useHiddenFolderFilter } from 'framework-listing-lib/internal/Folders/hooks/useFolderProps';
import { useFilterGroups, useHiddenFilter } from './useListingLibFilters';
import { useProperties } from '../internal/providers/PropertiesProvider';
function getDefaultParams(objectTypeId) {
  return {
    count: DEFAULT_PAGE_SIZE,
    offset: 0,
    objectTypeId,
    requestOptions: {
      allPropertiesFetchMode: 'latest_version',
      includeAllProperties: true
    },
    filterGroups: [{
      filters: []
    }],
    query: '',
    sorts: [{
      property: 'hs_created_at',
      order: DESC
    }, {
      property: 'hs_object_id',
      order: ASC
    }]
  };
}
function overrideSorts({
  sort,
  sortPropertyOverride
}) {
  var _sortPropertyOverride2;
  if (!sortPropertyOverride) {
    return [sort];
  }
  const isV1Override = !sortPropertyOverride.propertyMap && !sortPropertyOverride.transform;
  if (isV1Override) {
    var _sortPropertyOverride;
    return [Object.assign({}, sort, {
      property: ((_sortPropertyOverride = sortPropertyOverride[sort.property]) === null || _sortPropertyOverride === void 0 ? void 0 : _sortPropertyOverride.sortKey) || sort.property
    })];
  }
  const mappedSort = Object.assign({}, sort, {
    property: ((_sortPropertyOverride2 = sortPropertyOverride.propertyMap) === null || _sortPropertyOverride2 === void 0 || (_sortPropertyOverride2 = _sortPropertyOverride2[sort.property]) === null || _sortPropertyOverride2 === void 0 ? void 0 : _sortPropertyOverride2.sortKey) || sort.property
  });
  if (sortPropertyOverride.transform) {
    return sortPropertyOverride.transform(mappedSort);
  }
  return [mappedSort];
}

/**
 * Creates fetch params object from @filters object in useCrmObjectTypeListingFilters
 * @param {Object} filters
 */
function getFetchParams({
  deletedObjects,
  filterGroups,
  filtersState,
  hiddenFilter,
  includeHiddenFilter,
  quickFilters,
  sortPropertyOverride,
  timeZoneId
}) {
  const {
    associationPreviews,
    pagination,
    query,
    requestOptions,
    sort
  } = filtersState;
  const {
    count,
    offset
  } = pagination;
  const fetchParams = {
    count,
    filterGroups: getAggregatedFilterGroups({
      deletedObjects,
      filterGroups,
      hiddenFilter,
      includeHiddenFilter,
      quickFilters
    }),
    offset,
    query,
    timeZoneId
  };
  if (Object.keys(sort).length) {
    fetchParams.sorts = overrideSorts({
      sort,
      sortPropertyOverride
    });
  }
  if (!fetchParams.query) {
    fetchParams.query = '';
  }
  if (requestOptions) {
    fetchParams.requestOptions = requestOptions;
  }
  if (associationPreviews) {
    fetchParams.associationPreviews = associationPreviews;
  }
  return fetchParams;
}
/**
 * Returns the current crm-search query used in the UI
 */
export default function useCrmSearchQuery(t0) {
  "use memo";

  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {
      includeHiddenFilter: true
    } : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    includeHiddenFilter
  } = t1;
  const objectTypeId = useObjectTypeId();
  const hiddenFilter = useHiddenFilter();
  const {
    deletedObjects,
    filters
  } = useCrmObjectTypeListingFilters();
  const hiddenFolderFilter = useHiddenFolderFilter();
  const isFoldersMode = useIsFoldersMode();
  const filterGroups = useFilterGroups();
  const quickFilters = useQuickFilters();
  const {
    sortPropertyOverride
  } = useProperties();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = I18n.moment.userTz().format("Z");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const timeZoneId = t2;
  let t3;
  if ($[3] !== hiddenFilter || $[4] !== hiddenFolderFilter || $[5] !== includeHiddenFilter || $[6] !== isFoldersMode) {
    t3 = includeHiddenFilter ? {
      filterGroups: combineFilters(hiddenFilter ? hiddenFilter.filterGroups : [], isFoldersMode && hiddenFolderFilter ? hiddenFolderFilter.filterGroups : [])
    } : undefined;
    $[3] = hiddenFilter;
    $[4] = hiddenFolderFilter;
    $[5] = includeHiddenFilter;
    $[6] = isFoldersMode;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const combinedHiddenFilters = t3;
  const t4 = Boolean(includeHiddenFilter);
  let t5;
  if ($[8] !== combinedHiddenFilters || $[9] !== deletedObjects || $[10] !== filterGroups || $[11] !== filters || $[12] !== quickFilters || $[13] !== sortPropertyOverride || $[14] !== t4) {
    t5 = getFetchParams({
      deletedObjects,
      filterGroups,
      filtersState: filters,
      hiddenFilter: combinedHiddenFilters,
      includeHiddenFilter: t4,
      quickFilters,
      sortPropertyOverride,
      timeZoneId
    });
    $[8] = combinedHiddenFilters;
    $[9] = deletedObjects;
    $[10] = filterGroups;
    $[11] = filters;
    $[12] = quickFilters;
    $[13] = sortPropertyOverride;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const fetchParams = t5;
  let t6;
  if ($[16] !== objectTypeId) {
    t6 = getDefaultParams(objectTypeId);
    $[16] = objectTypeId;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== fetchParams || $[19] !== t6) {
    t7 = Object.assign({}, t6, fetchParams);
    $[18] = fetchParams;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}